/**
 * The app's navigation — the second surface drawn above the layouts.
 *
 * There was no navigation at all before this. Every screen carried its own Back
 * button pointing at wherever the person who wrote it assumed you had come from,
 * so Trends was reachable only through Ledger, and — because Sounding never
 * wired one up — `/ask` had no exit at all in an installed PWA, where there is
 * no browser chrome to fall back on.
 *
 * **This is chrome, not a layout (D-3).** It draws no content and encodes no
 * state; it stands in the same relationship to Horizon/Ledger/Trends/Sounding
 * that the session band does (`DESIGN_SYSTEM.md` §4.4). A fifth *layout* would
 * need the §4 argument made a third time. This does not.
 *
 * **No cards (D-1).** A hairline, the page's own ground, and five equal columns.
 * The one filled shape is the raised Add control — a control, which groups
 * nothing.
 *
 * Tab selection moves one shared indicator; phone tab-root navigation slides directionally.
 */

import { useHaptics } from "@/hooks/use_haptics";
import { DESTINATIONS as TABS } from "@/lib/tabs";
import { useTabNavigation } from "@/hooks/use_tab_navigation";
import { NavLink, useLocation } from "react-router-dom";

import { cx } from "@/features/common/primitives";

/**
 * Every tab tap asks the live navigation guard first.
 *
 * The bar is drawn outside the routes, so a screen holding unsaved edits has no
 * other way to be consulted before its draft is thrown away. Nothing is
 * registered on any other screen, so this is a `false` and a plain navigation
 * everywhere else (`nav_guard.ts`).
 */
function useTabTap(to: string): (event: React.MouseEvent) => void {
  const location = useLocation();
  const navigateTab = useTabNavigation();
  const haptic = useHaptics();
  return event => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (location.pathname + location.search === to) return;
    const direction = TABS.findIndex(tab => tab.to === to) > TABS.findIndex(tab => tab.to === location.pathname) ? "forward" : "back";
    if (navigateTab(to, direction)) haptic("select");
  };
}

/**
 * The five destinations, in bar order.
 *
 * **Add is third and raised because it is the product.** The numpad is the front
 * door (`SPEC.md` §3), it is what `/` serves, and putting it under the thumb at
 * the centre of the bar says so in the one place a user reads without being
 * told. The agent is deliberately *not* here: it is reached from a chip on
 * Capture and Ledger, because promoting it to a permanent destination would put
 * it level with the numpad, which is backwards (`SPEC.md` §13.1).
 */
interface Destination {
  to: string;
  label: string;
  icon: React.JSX.Element;
  raised?: boolean;
}

const ICONS = { "/home": <HomeIcon />, "/ledger": <LedgerIcon />, "/": <AddIcon />, "/analytics": <TrendsGlyph />, "/settings": <SettingsIcon /> };
const BAR_ITEMS: Destination[] = TABS.map(tab => ({ ...tab, icon: ICONS[tab.to] }));

/**
 * The routes that draw no *bottom* bar.
 *
 * Sounding owns its bottom edge with a dock that is a text field and a send
 * button; stacking a tab bar under a keyboard-adjacent composer on a 320px phone
 * makes both unusable, so it carries a back control in its header instead.
 *
 * **The desktop rail stays.** The conflict is with the phone's bottom edge and
 * nothing else — a left rail and a dock do not compete for the same 320px, and
 * hiding it would leave a desktop user on `/ask` with no navigation at all for
 * a reason that does not apply to them.
 */
const BOTTOM_BAR_HIDDEN_ON: readonly string[] = ["/ask"];

export function bottomBarHiddenOn(pathname: string): boolean {
  return BOTTOM_BAR_HIDDEN_ON.includes(pathname);
}

/**
 * The sidebar's width, as a Tailwind class and as the padding the routes need.
 *
 * One constant used twice rather than `w-56` here and `pl-phi-11` there: they are
 * the same measurement, and a drift between them puts the content under the
 * rail on exactly the screens nobody develops on.
 */
export const SIDEBAR_PAD = "wide-route-inset";

/**
 * Two presentations, two elements — deliberately, not as duplication.
 *
 * The phone bar's height has to reach `--shell-tabs` so every bottom-anchored
 * control in the app clears it, and it is **measured** rather than declared for
 * the reasons §4.4 already gives. A single element that is a bottom bar at 320px
 * and a full-height sidebar at 1440px would measure the whole viewport on
 * desktop and shorten every screen to nothing. Rendered as two, the phone bar is
 * `display: none` above `md`, its `offsetHeight` is 0, and `--shell-tabs`
 * becomes 0 with no media query in CSS and no `matchMedia` in JS.
 *
 * `selection_bar.tsx` splits itself the same way for the same kind of reason.
 */
export function TabBar({
  barRef,
  hideBottom = false,
}: {
  barRef: (node: HTMLElement | null) => void;
  hideBottom?: boolean;
}): React.JSX.Element {
  const location = useLocation();
  const pathname = location.pathname === "/family" ? "/home" : /^\/splits(?:\/|$)/.test(location.pathname) ? "/ledger" : location.pathname;
  const active = BAR_ITEMS.findIndex(destination => destination.to === "/" ? pathname === "/" : pathname.startsWith(destination.to));
  return (
    <>
      {hideBottom ? null : (
        <nav
          ref={barRef}
          aria-label="Main"
          className="feel-shell-tabs fixed inset-x-0 bottom-0 z-30 border-t border-line bg-bg pb-safe md:hidden"
        >
          {/* The sliding indicator is a sibling of the list, not an item in it:
              a list holds destinations, and an extra `li` would be counted as one. */}
          <div className="relative mx-auto w-full max-w-md">
            {active >= 0 ? <span aria-hidden="true" className="feel-tab-indicator feel-tab-phone" style={{ width: `${100 / BAR_ITEMS.length}%`, transform: `translateX(${String(active * 100)}%)` }} /> : null}
            <ul className="flex w-full items-stretch">
              {BAR_ITEMS.map((destination) => (
                <li key={destination.to} className="flex-1">
                  <PhoneTab destination={destination} />
                </li>
              ))}
            </ul>
          </div>
        </nav>
      )}

      <nav
        aria-label="Main"
        className="fixed inset-y-0 left-0 z-30 hidden wide-navigation border-r border-line bg-bg md:block"
      >
        <div className="relative mx-phi-n1 mt-phi-2">
          {active >= 0 ? <span aria-hidden="true" className="feel-tab-indicator feel-tab-rail" style={{ height: `${100 / BAR_ITEMS.length}%`, transform: `translateY(${String(active * 100)}%)` }} /> : null}
          <ul className="feel-rail-list">
            {BAR_ITEMS.map((destination) => (
              <li key={destination.to}>
                <DesktopTab destination={destination} />
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </>
  );
}

/**
 * `end` on `/` only.
 *
 * Without it the Add tab reads as active on every route, because every path
 * starts with a slash — and an app whose navigation always says you are on the
 * numpad has navigation that says nothing.
 */
function isExact(to: string): boolean {
  return to === "/";
}

function PhoneTab({ destination }: { destination: Destination }): React.JSX.Element {
  const onClick = useTabTap(destination.to);
  const pathname = useLocation().pathname;
  const familyHome = pathname === "/family" && destination.to === "/home";
  const splitLedger = /^\/splits(?:\/|$)/.test(pathname) && destination.to === "/ledger";
  return (
    <NavLink
      to={destination.to}
      end={isExact(destination.to)}
      data-tab-link
      onClick={onClick}
      className={({ isActive }) =>
        cx(
          "@container flex min-h-tab flex-col items-center justify-center gap-phi-n6 text-tab font-semibold",
          isActive || familyHome || splitLedger ? "text-acc" : "text-tx-2",
        )
      }
    >
      <span
        className={cx(
          "flex items-center justify-center",
          // The raised control: a filled circle proud of the bar's top edge.
          destination.raised === true &&
            "h-11 w-11 -translate-y-3.5 rounded-full bg-acc text-on-acc",
        )}
      >
        {destination.icon}
      </span>
      {/* A label is one word and cannot wrap, so at large text sizes it
          shrinks to its fifth of a 320px bar rather than clipping (the
          round 9 acceptance criterion); below that it is `text-tab`. */}
      <span
        className={cx(
          "tab-label max-w-full",
          destination.raised === true && "-mt-phi-n2",
        )}
      >
        {destination.label}
      </span>
    </NavLink>
  );
}

function DesktopTab({ destination }: { destination: Destination }): React.JSX.Element {
  const onClick = useTabTap(destination.to);
  const pathname = useLocation().pathname;
  const familyHome = pathname === "/family" && destination.to === "/home";
  const splitLedger = /^\/splits(?:\/|$)/.test(pathname) && destination.to === "/ledger";
  return (
    <NavLink
      to={destination.to}
      end={isExact(destination.to)}
      data-tab-link
      onClick={onClick}
      className={({ isActive }) =>
        cx(
          "flex min-h-[44px] flex-wrap items-center gap-phi-n2 rounded-phi-n2 px-phi-n2 py-phi-n2 text-phi-n1 font-semibold [overflow-wrap:anywhere]",
          isActive || familyHome || splitLedger ? "text-acc" : "text-tx-2",
        )
      }
    >
      <span className="flex items-center justify-center">{destination.icon}</span>
      {destination.label}
    </NavLink>
  );
}

/* ── the glyphs ──────────────────────────────────────────────────────────────
 *
 * All 20px, 1.8 stroke, `currentColor` — the shape language the header chips in
 * `primitives.tsx` already use, so the bar does not read as a second icon set.
 * Every one is `aria-hidden`: the visible label is the accessible name (D-4).
 */

function Glyph({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/** Two swells. Horizon is the waterline, and the waterline is the home screen. */
function HomeIcon(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M3 9c2.5-2.5 4.5-2.5 7 0s4.5 2.5 7 0" />
      <path d="M3 15c2.5-2.5 4.5-2.5 7 0s4.5 2.5 7 0" />
    </Glyph>
  );
}

/** Rules of even length — the Ledger is rows on hairlines and nothing else. */
function LedgerIcon(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M4 7h16M4 12h16M4 17h10" />
    </Glyph>
  );
}

/** A plus. The whole product in one glyph. */
function AddIcon(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M12 5v14M5 12h14" />
    </Glyph>
  );
}

/** The same rising line the Trends chip draws, so one idea keeps one shape. */
function TrendsGlyph(): React.JSX.Element {
  return (
    <Glyph>
      <path d="M4 17l5-5 3 3 7-7" />
      <path d="M15 8h5v5" />
    </Glyph>
  );
}

/** A dial with marks. Settings is where the cut-off day lives. */
function SettingsIcon(): React.JSX.Element {
  return (
    <Glyph>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4v3M12 17v3M4 12h3M17 12h3" />
    </Glyph>
  );
}
